import { get } from 'lodash';
import createCachedSelector from 're-reselect';

import { InternalBannersBaseInfoEditorStateProps } from '../../containers/InternalBannersBaseInfoEditorContainer/InternalBannersBaseInfoEditorContainer';
import { mapResourcesAndValuesInternal } from '../../mappers/banners-edit/banner-edit-internal/banner-edit-internal';
import { BannerId } from '../../typings/banners';
import {
    InternalPlaceTemplates,
    InternalTemplateId,
    InternalTemplateResource,
    InternalTemplateResourceRestrictions
} from '../../typings/internalBanners';
import {
    EditedInternalBanner,
    EditInternalBannersState
} from '../../typings/internalBannersEditState';

const getEditedBannerCacheKey = (
    state: EditInternalBannersState,
    bannerId: BannerId
): BannerId => bannerId;

export const selectInternalBannerResources = (
    state: EditInternalBannersState,
    campaignId: string,
    templateId: InternalTemplateId
): InternalTemplateResource[] | undefined => {
    const resources = state.templateResources[campaignId];
    const record = resources && resources[templateId];

    return record && record.resources;
};

export const selectInternalBannerResourceRestrictions = (
    state: EditInternalBannersState,
    campaignId: string,
    templateId: InternalTemplateId
): InternalTemplateResourceRestrictions[] | undefined => {
    const resources = state.templateResources[campaignId];
    const record = resources && resources[templateId];

    return record && record.resourceRestrictions;
};

export const selectEditedInternalBanner = (
    state: EditInternalBannersState,
    bannerId: BannerId
): EditedInternalBanner => state.banners[bannerId];

export const selectEditedInternalBannerPlaceTemplates = (
    state: EditInternalBannersState
): InternalPlaceTemplates => state.placeTemplates;

export const selectEditedInternalBannerTemplateResources = (
    state: EditInternalBannersState,
    bannerId: BannerId
): InternalTemplateResource[] | undefined => {
    const banner = state.banners[bannerId];

    return selectInternalBannerResources(
        state,
        banner.campaign.id,
        banner.templateId
    );
};

export const selectInternalBannerBaseInfo = createCachedSelector(
    selectEditedInternalBanner,
    selectEditedInternalBannerPlaceTemplates,
    selectEditedInternalBannerTemplateResources,
    (
        banner,
        placeTemplates,
        templateResources
    ): InternalBannersBaseInfoEditorStateProps => {
        const { description, templateId, templateVars } = banner;
        const templateVarsErrors = get(banner, 'errors.templateVars', {});
        const descriptionErrors = get(banner, 'errors.description.errors', []);
        const templateIdErrors = get(banner, 'errors.templateId.errors', []);
        const imageErrors = get(banner, 'errors.images.errors', []);

        const placeId = banner.campaign.placeId;

        return {
            baseInfo: {
                description: {
                    text: description,
                    valid: descriptionErrors.length === 0,
                    errors: descriptionErrors
                },
                templateId: {
                    text: templateId,
                    valid: templateIdErrors.length === 0,
                    errors: templateIdErrors
                }
            },
            templates: mapResourcesAndValuesInternal(
                templateId,
                templateResources,
                templateVars
            ),
            templateVarsErrors: Object.keys(templateVarsErrors).map(
                key => templateVarsErrors[key].errors[0]
            ),
            imageErrors: imageErrors,
            isNew: banner.isNew,
            templateIds: placeTemplates[placeId] || []
        };
    }
)(getEditedBannerCacheKey);
